import { Button } from '@ps-design-system/components/Button';
import { Spinner } from '@ps-design-system/components/Spinner';
import { Text } from '@ps-design-system/components/Text';
import PropTypes from 'prop-types';
import * as React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useCheckoutContext } from '../../CheckoutContextProvider';
import { getCurrencyFromListing } from '../../checkout-utils';

export const CreditsField = ({ errorMsg, customerCredits }) => {
  const { paymentForm, listing, quote, isSubmittingRequest } = useCheckoutContext();

  const appliedCreditsFromQuote =
    quote?.guestPaymentDetails?.subtotal_ps_credit;

  const appliedCreditsFromForm = paymentForm.watch('totalCreditsApplied');

  const selectedPaymentOption = paymentForm.watch('selectedPaymentOption');
  const currency = getCurrencyFromListing(listing);

  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(appliedCreditsFromForm !== appliedCreditsFromQuote);
  }, [appliedCreditsFromQuote, appliedCreditsFromForm]);

  const handleApplyCredits = () => {
    const { guestPaymentDetails } = quote || {};

    paymentForm.setValue(
      'totalCreditsApplied',
      guestPaymentDetails?.suggested?.subtotal_ps_credit
    );
  };

  const handleRemoveCredits = () => {
    paymentForm.setValue('totalCreditsApplied', 0);
  };

  const isDisabled = !quote?.spaceRentalContract ||
    selectedPaymentOption === 'pay_partial' ||
    !!errorMsg ||
    isSubmittingRequest;

  return (
    <div>
      <Text typeface="body-2-m">
        <FormattedMessage
          id="booking_request.review_request.credits"
          defaultMessage="Peerspace Credits"
        />
      </Text>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <Text data-testing-id="credit-balance" style={{ color: '#6a6e7a' }}>
          {appliedCreditsFromQuote > 0 &&
          selectedPaymentOption !== 'pay_partial' ? (
            <FormattedMessage
              id="inbox_guest_update_booking.apply_credit_field.applied_credits"
              defaultMessage="Applied {credits} to your booking"
              values={{
                credits: (
                  <FormattedNumber
                    value={appliedCreditsFromQuote}
                    currency={currency}
                    style="currency"
                  />
                )
              }}
            />
          ) : (
            <FormattedMessage
              id="inbox_guest_update_booking.apply_credit_field.your_balance"
              defaultMessage="Your credit balance: {balance}"
              values={{
                balance: (
                  <FormattedNumber
                    value={customerCredits}
                    currency={currency}
                    style="currency"
                  />
                )
              }}
            />
          )}
        </Text>

        {!!quote?.spaceRentalContract && isLoading ? (
          <Spinner appearance="black" />
        ) : (
          <Button
            variant="text-only"
            disabled={isDisabled}
            onClick={
              appliedCreditsFromForm > 0
                ? handleRemoveCredits
                : handleApplyCredits
            }
          >
            {appliedCreditsFromForm > 0 ? (
              <FormattedMessage
                id="inbox_guest_update_booking.apply_credit_field.remove"
                defaultMessage="Remove"
              />
            ) : (
              <FormattedMessage
                id="inbox_guest_update_booking.apply_credit_field.apply_credits"
                defaultMessage="Apply credits"
              />
            )}
          </Button>
        )}
      </div>

      {selectedPaymentOption === 'pay_partial' && (
        <Text element="p" style={{ color: '#6a6e7a', margin: '8px 0 0 0' }}>
          <FormattedMessage
            id="inbox_guest_update_booking.apply_credit_field.part_now_part_later_info"
            defaultMessage="Credits cannot be used with our Pay part now, part later feature."
          />
        </Text>
      )}

      {errorMsg && (
        <Text
          typeface="body-1"
          element="p"
          style={{ margin: '8px 0 0 0' }}
          className="tw-text-red-800"
        >
          {errorMsg}
        </Text>
      )}
    </div>
  );
};
CreditsField.displayName = 'CreditsField';
CreditsField.propTypes = {
  errorMsg: PropTypes.string,
  customerCredits: PropTypes.number
};
